import type {
	ReapplyRecipesJobSelection,
	ReapplyRecipesVariationSelection
} from "~produce/production/schedule/helpers/reapplyRecipesSelectionTypes";

import {
	type AssignmentOverrideChangeProjection,
	type EventIdChangeProjection,
	type RemovedVariationProjection,
	type ScalarFieldChangeProjection
} from "~/gql/graphqlBase.generated";
import { type GetReapplyRecipesPreviewQuery } from "~/gql/queries/produce/reapplyRecipes.generated";
import { eventIdChangeKey } from "~produce/production/schedule/helpers/reapplyRecipesSelectionTypes";

export type ReapplyChangeKind = "scalar" | "eventId" | "assignmentOverride" | "removedVariation";

type ReapplyChange =
	| ScalarFieldChangeProjection
	| EventIdChangeProjection
	| AssignmentOverrideChangeProjection
	| RemovedVariationProjection;

type Preview = GetReapplyRecipesPreviewQuery["reapplyRecipesPreview"][number];

const defaultSelectionForChange = (change: ReapplyChange, kind: ReapplyChangeKind): boolean => {
	switch (kind) {
		case "scalar":
		case "assignmentOverride":
			return !(change as ScalarFieldChangeProjection | AssignmentOverrideChangeProjection).hasExistingOverride;
		case "eventId":
			return true;
		case "removedVariation":
			return false;
	}
};

export const buildDefaultJobSelection = (preview: Preview): ReapplyRecipesJobSelection => {
	const variations: Record<string, ReapplyRecipesVariationSelection> = {};
	for (const variation of preview.diff.changedVariations) {
		variations[variation.variationId] = {
			scalarFieldNames: variation.scalarFieldChanges
				.filter((c) => defaultSelectionForChange(c, "scalar"))
				.map((c) => c.fieldName),
			eventIdChangeKeys: variation.eventIdChanges
				.filter((c) => defaultSelectionForChange(c, "eventId"))
				.map((c) => eventIdChangeKey(c.eventListName, c.eventId)),
			assignmentOverrideEventIds: variation.assignmentOverrideChanges
				.filter((c) => defaultSelectionForChange(c, "assignmentOverride"))
				.map((c) => c.eventId),
			regenerateTaskEvents: true
		};
	}
	const removedVariationIds = preview.diff.removedVariations
		.filter((rv) => defaultSelectionForChange(rv, "removedVariation"))
		.map((rv) => rv.variationId);

	return {
		enabled: !preview.recipeDeleted,
		variations,
		removedVariationIds
	};
};

export default defaultSelectionForChange;
